import * as React from 'react';
import { tenantConfig } from 'utils/tenant';
import { useError } from 'hooks/useError';
import { Outlet } from 'react-router';
import { EErrorCode } from 'config/errors-codes';
import { sendApiRequest } from 'utils/network';
import { user } from 'utils/auth/user';
import { isAuthenticated } from 'utils/auth';
import { applyDealerIdFromUrl } from 'utils/auth/applyDealerIdFromUrl';

interface IDealerAccessResponse {
  allowed: boolean;
  code?: EErrorCode;
}

/**
 * Validates dealer access against the backend whitelist.
 * Only runs for dealer users when whitelisting might be enabled.
 */
async function validateDealerAccess(
  brand: string,
  market: string,
  dealerId: string
): Promise<{ allowed: boolean; code?: EErrorCode }> {
  const response = await sendApiRequest<IDealerAccessResponse>(`/dealer-access?brand=${brand}&market=${market}`, {
    headers: {
      'X-Dealer': dealerId,
    },
  });

  return response ?? { allowed: true };
}

/**
 * SecureOutlet is a wrapper around the react-router Outlet component.
 * It performs additional checks to verify if the current tenant is allowed to access 3D Exhibition.
 */
export const SecureOutlet = () => {
  const throwError = useError();
  const [isValidating, setIsValidating] = React.useState(true);

  React.useEffect(() => {
    const validateAccess = async () => {
      try {
        await tenantConfig().configLoaded;

        // If the user is not authenticated, we do not need to verify the whitelist yet
        // With the early return, we will loop back to let the user complete the authentication flow
        // Once the user is authenticated, the dealer access can be evaluated properly
        if (!isAuthenticated()) {
          setIsValidating(false);
          return;
        }

        if (tenantConfig().inactive === true) {
          throwError(EErrorCode.DISABLED_MARKET);
          return;
        }

        // Apply the dealerId selected in AVP Portal (via URL param) before the whitelist check.
        // This ensures the whitelist is evaluated against the dealer the user actually selected,
        // not the first dealer in their list (which is the default before the URL param is applied).
        await applyDealerIdFromUrl(user);

        // Check dealer whitelist access for dealer users
        // Always validate dealer access - backend will check if whitelisting is enabled
        if (user.isDealer && user.dealerId) {
          const brand = tenantConfig().brand;
          const market = tenantConfig().market;

          if (brand && market) {
            const accessResult = await validateDealerAccess(brand, market, user.dealerId);

            if (!accessResult.allowed) {
              throwError(accessResult.code ?? EErrorCode.DEALER_NOT_WHITELISTED);
              return;
            }
          }
        }

        // Validation complete - allow rendering
        setIsValidating(false);
      } catch {
        throwError(EErrorCode.DISABLED_MARKET);
      }
    };

    validateAccess();
  }, [throwError]);

  // Block rendering until validation completes
  if (isValidating) {
    return null;
  }

  return <Outlet />;
};
